<template>
  <div id="hls-video-player">
    <p>{{ cameraMsg }}</p>
    <video-player
      id="video-player"
      class="media-video-player card-video vjs-big-play-centered "
      ref="videoPlayer"
      :options="playerOptions"
      @ready="onPlayerReadied"
      @play="onPlayerPlay"
      @pause="onPlayerPause"
      @error="onPlayerError"
      @waiting="onWaiting"
      @playing="onPlaying"

      @canplay="onCanplay"
      @canplaythrough="onCanplaythrough"
    />
  </div>
</template>

<script>
import {videoPlayer} from "vue-videojs7";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import moment from "moment";
// Mixin
import { CameraMixin } from "@/mixins";

export default {
  components: {videoPlayer},
  mixins: [CameraMixin],
  props: {
    streamName: {type: String , required: false},

    isLive: {type: Boolean, default: false},

    current: {type: Number, required: false},
    start: {type: Number, required: false},
    end: {type: Number, required: false},
    fps: {type: Number, required: false},

    isPause: {type: Boolean, required: false},
    controls: {type: Boolean, default: false},
    autoPlay: {type: Boolean, default: false},
    hidePlayButton: {type: Boolean, default: false},

    width: {type: Number, required: false},
    videoInternalStartTime: {type: Date, default: null},
    videoInternalEndTime: {type: Date, default: null},

    camera: {type: Object, required: false},
  },

  data() {
    return {
      cameraStateOptions: Object.freeze({
        preparing: 1,
        playable: 2,
        playing: 3,
        failed: 4,
      }),
      cameraState: 0,

      cameraMsg: "",

      hlsSrc: null,
      tech: null,
      playerOptions: {
        playbackRates: [1, 2, 4, 8, 16, 32],
        fluid: this.width ? false : true,
        width: this.width,
        autoplay: this.autoPlay,
        muted: true,
        controls: this.controls,
        preload: "auto",
        fullscreen: false,
        bigPlayButton: false,
        flash: {vls: {withCredentials: false}},
        html5: {
          vls: {
            withCredentials: false,
            liveRangeSafeTimeDelta: 20000
          }
        },
        language: "ja",
        languages: {
          ja: {
            Play: "再生",
            "The media could not be loaded, either because the server or network failed or because the format is not supported.":
              "ネットワークに障害が発生したか、カメラが停止したため、再生できません"
          }
        },
        controlBar: {pictureInPictureToggle: false}
      },
      initialized: false,

      goalBufferLength: 600, // 600秒後までbufferする(defaultは30)
      maxGoalBufferLength: 650,

      isPropertyChanged: false,
      hasVideoError: false,

      isAttemptedDirectConnect: false
    };
  },
  watch: {
    current(value) {
      this.player.currentTime(value - this.start)
    },
    fps(value) {
      if(this.player) {
        if(value === 32) return // playbackRateは16までしかは対応していない
        this.player.playbackRate(value)
      }
    },
    isPause(value) {
      if(this.player) {
        value ? this.player.pause() : this.player.play()
      }
    },

    cameraState(value) {
      const {preparing, playable, playing, failed} = this.cameraStateOptions
      switch(value) {
        case preparing:
          this.hasVideoError = false
          this.player.error(null)
          if (!this.hidePlayButton) {
            this.hideVideoPlayButton()
          }
          this.cameraMsg = "接続中..."
          break
        case playable:
          this.hasVideoError = false
          this.player.error(null)
          if (!this.hidePlayButton) {
            this.showVideoPlayButton()
          }
          this.cameraMsg = ""
          break
        case playing:
          this.hasVideoError = false
          this.player.error(null)
          if (!this.hidePlayButton) {
            this.hideVideoPlayButton()
          }
          this.cameraMsg = ""
          break
        case failed:
          this.hasVideoError = true
          if (!this.hidePlayButton) {
            this.hideVideoPlayButton()
          }
          this.cameraMsg = ""
          this.player.error(
            "映像が見つかりません"
          )
          break
      }
    }
  },
  computed: {
    player() {
      if (typeof this.$refs.videoPlayer === "undefined") return null;
      return this.$refs.videoPlayer.player;
    },
  },
  async created() {
    if (this.isLive) this.playerOptions.playbackRates = [1]
  },
  async mounted() {
    this.cameraState = this.cameraStateOptions.preparing
    this.attemptToConnect()
  },
  methods: {
    async onPlayerReadied(_player) {
      if (!this.initialized) {
        this.initialized = true;
      }
    },

    onPlayerPlay(_player) {
      this.cameraState = this.cameraStateOptions.playing
    },

    onPlayerError(_e) {
      if (!this.isAttemptedDirectConnect) {
        this.isAttemptedDirectConnect = true
        this.directConnect();
      }
    },

    onCanplaythrough() {
    },

    onPlayerPause() {
    },

    onWaiting() {
      this.safeBufferPause()
    },

    onPlaying() {
      this.safeBufferPause()
    },

    async onCanplay(player) {
      if(!this.isPropertyChanged) {
        let segments = this.player.tech(true).vhs.playlists.media().segments
        // 動画の開始時間と終了時間を算出
        this.$emit('update:videoInternalStartTime', segments[0].dateTimeObject)
        this.$emit('update:videoInternalEndTime', segments[segments.length - 1].dateTimeObject)

        // videojsのプロパティを初回一度だけ変更
        // player.vhs.options_.externVhs.MAX_GOAL_BUFFER_LENGTH = this.maxGoalBufferLength
        // player.vhs.options_.externVhs.GOAL_BUFFER_LENGTH = this.goalBufferLength
        this.isPropertyChanged = true
      }
    },

    hideVideoPlayButton() {
      let playButton = this.player
        .contentEl()
        .getElementsByClassName("vjs-big-play-button");
      if (playButton.length !== 0) {
        playButton[0].style.display = "none";
      }
    },

    showVideoPlayButton() {
      let playButton = this.player
        .contentEl()
        .getElementsByClassName("vjs-big-play-button");
      if (playButton.length !== 0) {
        playButton[0].style.display = "block";
      }
    },

    playVideo(url) {
      if (this.player) {
        const video = {
          withCredentials: false,
          type: "application/x-mpegurl",
          src: url
        };
        this.hlsSrc = url
        this.player.src(video);
      }
    },

    async attemptToConnect() {
      await this.directConnect()
    },

    safeBufferPause() {
      let bufferEnd = this.player.buffered().end(0)
      let current = this.player.currentTime()
      // 8倍速以上で、かつバッファが一定秒未満のとき再生を止める
      if (this.player.playbackRate() >= 8 && current + this.safeBufferThreshold > bufferEnd) {
        this.$store.dispatch("enterpriseVesselVoyageHistory/playTicker", false);
        this.player.pause()
        this.$vs.notify({
          title: "動画バッファ不足：再生速度を下げるか、しばらくしてから試してください",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square"
        });
      }
    },

    async directConnect() {
      let url = await this.connectToCamera();
      if (url.error_code) {
        this.cameraState = this.cameraStateOptions.failed
      } else {
        this.playDirectVideo(url);
      }
    },

    async connectToCamera() {
      const payload = {
        started_at: moment.unix(this.start).utc().toISOString(),
        ended_at: moment.unix(this.end).utc().toISOString()
      };
      const res = await AiseaApiFactory.get("enterpriseCamera").onDemandByHash(
        this.camera.hash, payload
      );

      return res.data;
    },

    playDirectVideo(m3u8String) {
      const blob = new Blob([m3u8String], { type: 'application/x-mpegurl' });
      const blobUrl = URL.createObjectURL(blob);
      if (this.player) {
        const video = {
          withCredentials: false,
          type: "application/x-mpegurl",
          src: blobUrl
        };
        this.player.src(video);
        this.cameraState = this.cameraStateOptions.playable
      }
    },
  },
  beforeDestroy() {
    this.is_ready = false;
    this.videoList = []
  }
};
</script>

<style lang="scss">
#video-player {
  .video-js .vjs-time-control {
      display: block;
  }
  .video-js .vjs-remaining-time {
      display: none;
  }
}
</style>
<style lang="scss" scoped>
#hls-video-player {
  position: relative;
  .no-video {
    background-color: #000000;
  }
  button {
    z-index: 5;
    position: absolute;
    top: 65%;
    left: 50%;
    transform: translateX(-50%);
    width: fit-content;
  }
  p {
    position: absolute;
    top: 50%;
    left: 50%;
    -ms-transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    margin: 0;
    padding: 0;
    color: white;
    z-index: 5;
  }
}
</style>
